import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { Link, Stack } from '@mui/material'
import { ButtonSecondary } from 'components/ButtonSecondary'
import { ItemOption } from 'components/FormInputSelect'
import React, { useEffect } from 'react'
import {
  Control,
  FieldError,
  useFieldArray,
  UseFormClearErrors,
  useFormContext,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { documentInit, DocumentType } from 'types/document.type'
import { HearingIdentificationType } from 'types/hearing-identification.type'
import { DocumentItem } from './components/DocumentItem'
import { validateDocument } from 'utils/validate.util'

const MAX_LIMIT_DOCUMENTS = 5

const TJ_JURIDICAL_PORTAL = 'https://www4.tjrj.jus.br/Portal-Extrajudicial/CNO/'

type DocumentListProps = {
  control: Control<HearingIdentificationType>
  watch: UseFormWatch<HearingIdentificationType>
  setValue: UseFormSetValue<HearingIdentificationType>
  setError: UseFormSetError<HearingIdentificationType>
  clearErrors: UseFormClearErrors<HearingIdentificationType>
  typeOptions: ItemOption[]
}

const debounce = (func: () => void, wait: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: any[]) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(), wait)
  }
}

export const DocumentList: React.FC<DocumentListProps> = ({
  control,
  watch,
  setValue,
  setError,
  clearErrors,
  typeOptions,
}) => {
  const {
    fields: documentsFields,
    append: handleAddDocument,
    remove: handleRemoveDocument,
  } = useFieldArray({
    control,
    name: 'documentos',
  })

  const documentsValues: DocumentType[] | undefined | null = watch('documentos')

  const isFilled = (): boolean => {
    const lastItem = documentsValues && documentsValues[documentsValues.length - 1]

    return lastItem ? !validateDocument(lastItem) : true
  }

  const handleDisableAddButton = (): boolean => {
    return documentsValues && documentsValues.length < MAX_LIMIT_DOCUMENTS ? isFilled() : true
  }

  useEffect(() => {
    if (documentsValues && !documentsValues.length) {
      setValue('documentos', [documentInit], { shouldDirty: false })
    }
  }, [documentsValues, setValue])

  const { getFieldState } = useFormContext<HearingIdentificationType>()
  const stateDocumentos = getFieldState('documentos')

  useEffect(() => {
    const errors = stateDocumentos.error as Record<string, FieldError>[] | undefined
    if (errors) {
      const hasTipoDocumentoError = errors.some((error) => error?.idTipoDocumento)
      if (hasTipoDocumentoError) {
        debounceVerifyAndClearErrors()
      }
    }
  }, [stateDocumentos])

  const verifyAndClearErrors = () => {
    const tipoCount: Record<string, number[]> = {}
    documentsValues?.forEach((doc, index) => {
      if (tipoCount[doc.idTipoDocumento]) {
        tipoCount[doc.idTipoDocumento].push(index)
      } else {
        tipoCount[doc.idTipoDocumento] = [index]
      }
    })

    Object.values(tipoCount).forEach((indices) => {
      if (indices.length < 2) {
        indices.forEach((indice) => {
          clearErrors(`documentos.${indice}.idTipoDocumento`)
        })
      }
    })
  }

  const debounceVerifyAndClearErrors = debounce(verifyAndClearErrors, 500)

  return (
    <>
      {documentsFields.map((_, i) => (
        <DocumentItem
          key={i}
          index={i}
          control={control}
          watch={watch}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          typeOptions={typeOptions}
          onRemove={handleRemoveDocument}
          isRemovable={documentsFields.length > 1}
        />
      ))}
      <Stack direction={'row'} spacing={4} sx={{ ml: 2, width: '100%' }}>
        <ButtonSecondary
          maxWidth={150}
          startIcon={<AddRoundedIcon />}
          label='Documento'
          disabled={handleDisableAddButton()}
          onClick={() => handleAddDocument(documentInit)}
        />
        <Link href={TJ_JURIDICAL_PORTAL} target='_blank'>
          Consultar Certidões
        </Link>
      </Stack>
    </>
  )
}
